/**
 * Checkout: datos, entrega y documento (CHK-03, CHK-04, CHK-05, CHK-06).
 *
 * No se indexa. Es el pedido de una persona en un navegador, igual que el carro,
 * y ademas depende de un carro que vive en `localStorage`: la URL no lleva
 * nada, y entrar aca sin carro no muestra un checkout vacio, devuelve al carro.
 *
 * **Cuando se verifica el stock.** Al entrar, y de nuevo cada vez que el carro
 * cambia. La regla de negocio dice que la verificacion ocurre al momento de
 * pagar y no al agregar al carro; esto no la contradice, la adelanta: es mejor
 * avisar que falta algo ANTES de que la persona escriba su direccion que
 * despues. El boton de pagar vuelve a verificar (el backend lo hace de nuevo
 * al crear el pedido, CHK-05): entre que se carga esta pantalla y se aprieta
 * pagar pueden pasar minutos.
 *
 * **El pago.** "Pagar" crea el pedido en el backend y redirige a Webpay
 * (PAG-01). Si el backend responde que ya no hay stock, se fuerza una nueva
 * verificacion (`intentoPago` entra en las claves de `usePeticion`) para que
 * el aviso de arriba se actualice con lo que realmente falta.
 *
 * **El formulario sobrevive a un pago rechazado.** Pagar es una navegacion
 * completa fuera del sitio (Webpay) y de vuelta; React pierde todo su estado
 * en el camino. `datos` se guarda en `localStorage` en cada cambio
 * (`almacen-checkout.ts`, mismo criterio que el carro en CHK-02) y se lee de
 * ahi al montar: volver de un rechazo con "Volver al checkout" encuentra el
 * formulario tal como se dejo, no en blanco. Se borra recien cuando el pago
 * se aprueba (`ConfirmacionPedido.tsx`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';

import type { CrearPedidoDto, RespuestaVerificacionStock } from '@bandu/compartido';

import { AvisoDisponibilidad } from '../componentes/checkout/AvisoDisponibilidad';
import { PanelContacto } from '../componentes/checkout/PanelContacto';
import { PanelDocumento } from '../componentes/checkout/PanelDocumento';
import { PanelEntrega } from '../componentes/checkout/PanelEntrega';
import { PasosCompra } from '../componentes/checkout/PasosCompra';
import { ResumenPedido } from '../componentes/checkout/ResumenPedido';
import { Cargando } from '../componentes/comunes/EstadoPeticion';
import { useCarro } from '../contexto/carro-contexto';
import { useDespacho } from '../contexto/despacho-contexto';
import { usePeticion } from '../hooks/use-peticion';
import { registrarInicioCheckout } from '../medicion/comercio';
import { Seo } from '../seo/Seo';
import { verificarCarro } from '../servicios/carro.servicio';
import { ErrorDeApi } from '../servicios/cliente-api';
import { crearPedido } from '../servicios/pedido.servicio';
import { guardarCheckout, leerCheckoutGuardado } from '../utilidades/almacen-checkout';
import type { CampoCheckout, DatosCheckout } from '../utilidades/checkout';
import {
  CHECKOUT_INICIAL,
  costoDespacho as calcularCostoDespacho,
  facturacionDelPedido,
  hayErrores,
  regionElegida,
  validarCheckout,
} from '../utilidades/checkout';
import { entregaVigente } from '../utilidades/entrega';
import { redirigirAWebpay } from '../utilidades/redirigir-a-webpay';
import { rutas } from '../utilidades/rutas';
import { whatsappPedido } from '../utilidades/whatsapp';

const TODOS_LOS_CAMPOS = Object.keys(CHECKOUT_INICIAL) as CampoCheckout[];

export function Checkout() {
  const { carro, cambiarCantidad, quitar } = useCarro();
  const [datosElegidos, setDatos] = useState<DatosCheckout>(leerCheckoutGuardado);

  // Cada cambio se guarda. Es el unico lugar que escribe en el
  // almacenamiento, igual que `ProveedorCarro` con el carro.
  useEffect(() => {
    guardarCheckout(datosElegidos);
  }, [datosElegidos]);

  // Las tarifas ya vienen cargadas por el armazon de la tienda: la cabecera
  // anuncia el mismo umbral, asi que pedirlas de nuevo seria una peticion
  // repetida por vista.
  const { opciones: opcionesDespacho } = useDespacho();

  // Lo que vale es la entrega elegida SI se sigue ofreciendo: el panel puede
  // haberla apagado, o venir de un checkout guardado de otro dia. Si no, la
  // primera que se ofrezca hoy. Se deriva en vez de corregir el estado, asi
  // la eleccion del comprador vuelve sola si el panel la enciende de nuevo.
  const datos = useMemo(
    () => ({
      ...datosElegidos,
      tipoEntrega: entregaVigente(datosElegidos.tipoEntrega, opcionesDespacho),
    }),
    [datosElegidos, opcionesDespacho],
  );

  // Un campo se marca en rojo recien cuando la persona sale de el. Validar
  // mientras escribe convierte el formulario en una lista de reproches.
  const [tocados, setTocados] = useState<ReadonlySet<CampoCheckout>>(new Set());

  const [pagando, setPagando] = useState(false);
  const [errorPago, setErrorPago] = useState<string | null>(null);
  // Se incrementa para forzar una nueva verificacion de stock cuando el
  // backend rechaza el pago por falta de stock (no cambia el carro en si,
  // asi que `firmaCarro` solo no dispara el refetch de `usePeticion`).
  const [intentoPago, setIntentoPago] = useState(0);

  /**
   * Identidad del carro para la verificacion: los SKU con su cantidad. No sirve
   * el carro entero como dependencia —se reconstruye en cada render— ni su
   * subtotal, que cambia cuando se refrescan los precios sin que haya nada
   * nuevo que verificar.
   */
  const firmaCarro = carro.items.map((item) => `${item.producto.sku}x${item.cantidad}`).join('|');

  // Una vez por entrada al checkout (GA), y solo con algo que pagar.
  const inicioRegistrado = useRef(false);
  useEffect(() => {
    if (inicioRegistrado.current || carro.items.length === 0) return;
    inicioRegistrado.current = true;
    registrarInicioCheckout(carro);
  }, [carro]);

  const verificacion = usePeticion<RespuestaVerificacionStock | null>(
    // Con el carro vacio no se pregunta nada: quitar la ultima linea desde el
    // aviso de faltantes deja el carro en cero por un instante, y verificar
    // eso seria una peticion condenada a fallar mientras se navega afuera.
    (senal) => (carro.items.length === 0 ? Promise.resolve(null) : verificarCarro(carro, senal)),
    [firmaCarro, intentoPago],
  );

  const cambiar = useCallback((cambio: Partial<DatosCheckout>) => {
    setDatos((actuales) => ({ ...actuales, ...cambio }));
  }, []);

  const marcarTocado = useCallback((campo: CampoCheckout) => {
    setTocados((actuales) => new Set(actuales).add(campo));
  }, []);

  const errores = useMemo(() => validarCheckout(datos), [datos]);

  // Solo se muestran los errores de los campos ya tocados.
  const erroresVisibles = useMemo(() => {
    const visibles: typeof errores = {};
    for (const [campo, mensaje] of Object.entries(errores)) {
      if (tocados.has(campo as CampoCheckout)) visibles[campo as CampoCheckout] = mensaje;
    }
    return visibles;
  }, [errores, tocados]);

  const costoDespacho = calcularCostoDespacho(datos, opcionesDespacho, carro.subtotal);
  const despachoGratis =
    opcionesDespacho !== null && carro.subtotal >= opcionesDespacho.despachoGratisDesde;

  const enlaceWhatsapp = whatsappPedido(
    carro,
    datos,
    costoDespacho,
    regionElegida(datos, opcionesDespacho)?.nombre ?? '',
  );

  const alPagar = useCallback(async () => {
    if (hayErrores(errores)) {
      // Recien ahora se marcan todos: es el mismo criterio de "submit" de un
      // formulario comun, y evita una pantalla roja completa desde el primer
      // campo que se toca.
      setTocados(new Set(TODOS_LOS_CAMPOS));
      return;
    }

    setErrorPago(null);
    setPagando(true);

    const dto: CrearPedidoDto = {
      items: carro.items.map((item) => ({ sku: item.producto.sku, cantidad: item.cantidad })),
      contacto: { nombre: datos.nombre.trim(), email: datos.email.trim(), telefono: datos.telefono.trim() },
      tipoDocumento: datos.tipoDocumento,
      facturacion: datos.tipoDocumento === 'factura' ? facturacionDelPedido(datos) : null,
      rutComprador:
        datos.tipoDocumento === 'boleta' && datos.rutComprador.trim().length > 0
          ? datos.rutComprador.trim()
          : null,
      tipoEntrega: datos.tipoEntrega,
      direccion:
        datos.tipoEntrega === 'despacho'
          ? {
              region: datos.codigoRegion,
              comuna: datos.comuna,
              calle: datos.calle,
              numero: datos.numero,
              departamento: datos.departamento.trim() || null,
              referencia: datos.referencia.trim() || null,
            }
          : null,
      aceptaPromociones: datos.aceptaPromociones,
    };

    try {
      const inicioPago = await crearPedido(dto);
      // No se limpia `pagando`: la pagina esta a punto de navegar a Webpay.
      redirigirAWebpay(inicioPago.urlPago, inicioPago.token);
    } catch (error) {
      setPagando(false);

      if (error instanceof ErrorDeApi && error.codigo === 'sin_stock') {
        setErrorPago('El stock cambio mientras completabas tus datos. Revisa el aviso de arriba.');
        setIntentoPago((n) => n + 1);
        return;
      }

      setErrorPago(
        error instanceof ErrorDeApi
          ? error.message
          : 'No pudimos conectarnos con el servidor. Intenta nuevamente.',
      );
    }
  }, [carro, datos, errores]);

  // Sin carro no hay checkout. Va despues de los hooks: React exige que se
  // llamen siempre, en el mismo orden.
  if (carro.items.length === 0) return <Navigate to={rutas.carro()} replace />;

  return (
    <>
      <Seo
        titulo="Finalizar compra"
        descripcion="Completa tus datos de entrega y facturacion para finalizar tu compra."
        ruta={rutas.checkout()}
        sinIndexar
      />

      <div className="contenedor pagina">
        <header className="encabezado-pag">
          <div>
            <h1>Finalizar compra</h1>
            <p className="sub">Completa tus datos para cerrar el pedido.</p>
          </div>
        </header>

        <PasosCompra actual={2} />

        {verificacion.estado === 'cargando' ? <Cargando que="la disponibilidad" /> : null}

        {verificacion.datos !== null ? (
          <AvisoDisponibilidad
            verificacion={verificacion.datos}
            carro={carro}
            alAjustar={cambiarCantidad}
            alQuitar={quitar}
          />
        ) : null}

        {verificacion.estado === 'error' ? (
          <p className="ck-alerta" role="status">
            No pudimos revisar el stock en este momento. Puedes continuar: confirmamos la
            disponibilidad antes de despachar.
          </p>
        ) : null}

        <div className="checkout">
          <form onSubmit={(evento) => evento.preventDefault()} noValidate>
            <PanelContacto
              datos={datos}
              errores={erroresVisibles}
              alCambiar={cambiar}
              alSalirDelCampo={marcarTocado}
            />

            <PanelEntrega
              datos={datos}
              errores={erroresVisibles}
              opciones={opcionesDespacho}
              subtotal={carro.subtotal}
              alCambiar={cambiar}
              alSalirDelCampo={marcarTocado}
            />

            <PanelDocumento
              datos={datos}
              errores={erroresVisibles}
              opciones={opcionesDespacho}
              alCambiar={cambiar}
              alSalirDelCampo={marcarTocado}
            />
          </form>

          <ResumenPedido
            carro={carro}
            costoDespacho={costoDespacho}
            despachoGratis={despachoGratis}
            tipoEntrega={datos.tipoEntrega}
            enlaceWhatsapp={enlaceWhatsapp}
            alPagar={() => {
              void alPagar();
            }}
            pagando={pagando}
            pagoDeshabilitado={verificacion.datos?.resultado === 'faltante'}
            errorPago={errorPago}
          />
        </div>
      </div>
    </>
  );
}
